import { Box, CircularProgress, IconButton, MenuItem, Stack, Typography } from '@mui/material'
import List from '@mui/material/List'
import NavigationIcon from '@mui/icons-material/Navigation'
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ItineraryLeg from 'src/components/plan/PlanItineraryLeg'
import AutomaticSheet from 'src/components/sheet/AutomaticSheet'
import SheetHeader from 'src/components/sheet/SheetHeader'
import { MapRenderContext } from 'src/contexts/MapRenderContext'
import { useGetPlanPlanTransportModesFromLonLatToLonLatGet, useGetVehicleLocationsTripsTripIdVehiclesGet } from 'src/generated/brezavta/queries'
import { Plan } from 'src/generated/brezavta/requests'
import { addToCalendar, planToIcal } from 'src/lib/icalUtil'
import { getColorForMode } from 'src/lib/modes'
import { calculateBoundsForLegs, useLayerData, registerVehicleImages, colorToImageId, normalizeVehicleColor, darkenColor } from 'src/lib/util'

export const Route = createFileRoute('/plan/$mode/$coordinates/$datetime/$type/$page/$itinIndex')({
    component: () => <ShowPlanDetails />,
})

const VEHICLE_REFRECH_INTERVAL_MS = 15000

function ShowPlanDetails() {
    const { t } = useTranslation()
    const { mode, coordinates, datetime, page, itinIndex } = Route.useParams()
    const state = useRouterState()
    const navigate = useNavigate()
    const { map, mapLoaded } = useContext(MapRenderContext)

    const [data, setData] = useState<Plan>()

    const { data: planData } = useGetPlanPlanTransportModesFromLonLatToLonLatGet({
        path: {
            from_lon_lat: coordinates!.split(';')[0],
            to_lon_lat: coordinates!.split(';')[1],
            transport_modes: mode,
        },
        query: {
            date: datetime!.split('T')[0],
            time: datetime!.split('T')[1].substring(0, 5),
            page_cursor: page == 'null' ? '' : page,
        },
    })

    const [tripIds, setTripIds] = useState<string>('')

    const { data: vehicleData, refetch: vehicleRefetch } = useGetVehicleLocationsTripsTripIdVehiclesGet({
        path: { trip_id: tripIds },
    }, undefined, { enabled: tripIds != '' })

    // Initialize the Plan from router state
    useEffect(() => {
        if (!data && state.location.state?.plan) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setData(state.location.state.plan)
        }
    }, [state, data])

    // Update the Plan when the request returns
    // TODO: how to avoid making a request when we already have router state
    useEffect(() => {
        if (planData) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setData(planData.plans[itinIndex])
        }
    }, [planData, setData, itinIndex])

    /*
    ROUTE RENDERING ON MAP
  */

    const layerData = data && {
        type: 'FeatureCollection',
        features: data.legs.map(itinerary => ({
            type: 'Feature',
            geometry: itinerary.geometry,
            properties: {
                color: getColorForMode(itinerary),
                casingColor: darkenColor(getColorForMode(itinerary), 0.45),
            },
        })),
    }

    useLayerData('planView', layerData, map, mapLoaded)

    // set map bounds to fit the route
    useEffect(() => {
        if (!mapLoaded || !data) return
        const bounds = calculateBoundsForLegs(data.legs)
        map!.current!.fitBounds(bounds, {
            padding: 50,
        })
    }, [data, mapLoaded, map])

    /*
    VEHICLE LOCATIONS
  */

    // Extract trip IDs for fethching vehicle locations
    useEffect(() => {
        if (!data) return
        // Also filters out undefined routes
        const tripIds = data.legs.map(itinerary => itinerary.trip?.gtfs_id).filter(id => id)
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setTripIds(tripIds.join(','))
    }, [data])

    const vehicleLocationsData = vehicleData && {
        type: 'FeatureCollection',
        features: vehicleData.map(vehicle => ({
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [vehicle.lon, vehicle.lat],
            },
            properties: {
                color: normalizeVehicleColor(vehicle.color),
                color_id: colorToImageId(normalizeVehicleColor(vehicle.color)),
                heading: vehicle.heading ?? 0,
            },
        })),
    }
    useLayerData('planVehicleLocations', vehicleLocationsData, map, mapLoaded)

    // Register vehicle images when vehicle data changes
    useEffect(() => {
        if (!vehicleData || !map?.current) return
        const uniqueColors = [...new Set(vehicleData.map((v: typeof vehicleData[number]) => normalizeVehicleColor(v.color)))] as string[]
        registerVehicleImages(map.current, uniqueColors)
    }, [vehicleData, map])

    // refetch locations once every 15 seconds
    useEffect(() => {
        if (tripIds) {
            setTimeout(() => {
                vehicleRefetch()
            }, VEHICLE_REFRECH_INTERVAL_MS)
        }
    }, [tripIds, vehicleRefetch])

    if (!data) {
        return (
            <AutomaticSheet>
                <Box sx={{ display: 'flex', height: window.innerHeight / 2, justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress />
                </Box>
            </AutomaticSheet>
        )
    }

    return (
        <AutomaticSheet>
            <SheetHeader
                heading={t('Trip details')}
                actions={(
                    <IconButton
                        size="small"
                        color="primary"
                        aria-label={t('Start Navigation')}
                        onClick={() => navigate({ to: '/plan/navigate', state: { plan: data } })}
                    >
                        <Stack direction="row" alignItems="center" gap={0.5}>
                            <NavigationIcon fontSize="small" />
                            <Typography variant="caption" fontWeight={700} sx={{ lineHeight: 1 }}>{t('Start')}</Typography>
                        </Stack>
                    </IconButton>
                )}
                menuItems={[
                    <MenuItem
                        onClick={() => {
                            const ics = planToIcal(data)
                            addToCalendar(ics)
                        }}
                        key="download_ical"
                    >
                        {t('Add to calendar')}
                    </MenuItem>,
                ]}
            />
            <List sx={{ width: '100%', overflowX: 'hidden' }}>
                {
                    data.legs.map((itinerary, index) => {
                        const nextLeg = data.legs[index + 1]
                        const previousLeg = data.legs[index - 1]
                        return (
                            <div key={index}>
                                <ItineraryLeg
                                    leg={itinerary}
                                    nextLeg={nextLeg}
                                    previousLeg={previousLeg}
                                    isFirst={index === 0}
                                    isLast={index === data.legs.length - 1}
                                />
                            </div>
                        )
                    })
                }
            </List>
        </AutomaticSheet>
    )
}
